import type { UserActor } from "@corpuscomplete/core/actor";
import type { ExpressionBuilder, Transaction } from "@rocicorp/zero";

import { AccessGrant } from "../../auth/grant";
import { Scope } from "../../auth/scope";
import { zql } from "../../zero/schema";
import type { Schema } from "../../zero/schema";

export namespace MealPlanningSessionScopePolicy {
  const contentsViewCriteria = AccessGrant.criteria("meal_planning_session", "contents", [
    "editor",
    "viewer",
  ]);
  const contentsWriteCriteria = AccessGrant.criteria("meal_planning_session", "contents", [
    "editor",
  ]);

  export function viewable(eb: ExpressionBuilder<"mealPlanningSession", Schema>, actor: UserActor) {
    return eb.exists(
      "grants",
      (grant) => AccessGrant.matching(grant, actor, contentsViewCriteria),
      {
        flip: true,
      },
    );
  }

  export function editable(eb: ExpressionBuilder<"mealPlanningSession", Schema>, actor: UserActor) {
    return eb.exists(
      "grants",
      (grant) => AccessGrant.matching(grant, actor, contentsWriteCriteria),
      {
        flip: true,
      },
    );
  }

  export async function canWrite(
    tx: Transaction,
    actor: UserActor,
    scope: Scope.Info<"mealPlanningSession">,
  ): Promise<boolean> {
    return AccessGrant.check(tx, actor, contentsWriteCriteria, scope.mealPlanningSessionId);
  }

  export async function canEditContents(
    tx: Transaction,
    actor: UserActor,
    mealPlanningSessionId: string,
  ): Promise<boolean> {
    return AccessGrant.check(tx, actor, contentsWriteCriteria, mealPlanningSessionId);
  }

  export async function canCreatePlannedMealDrafts(
    tx: Transaction,
    actor: UserActor,
    args: { mealPlanningSessionId: string; scopeId: string },
  ): Promise<boolean> {
    const session = await tx.run(
      zql.mealPlanningSession
        .where("id", "=", args.mealPlanningSessionId)
        .where("scopeId", "=", args.scopeId)
        .one(),
    );
    if (!session) {
      return false;
    }
    return canEditContents(tx, actor, args.mealPlanningSessionId);
  }

  export async function canEditPlannedMealDraft(
    tx: Transaction,
    actor: UserActor,
    args: { plannedMealId: string; scopeId: string },
  ): Promise<boolean> {
    return (await editablePlannedMealDraftSessionId(tx, actor, args)) !== null;
  }

  export async function editablePlannedMealDraftSessionId(
    tx: Transaction,
    actor: UserActor,
    args: { plannedMealId: string; scopeId: string },
  ): Promise<string | null> {
    const links = await tx.run(
      zql.mealPlanningSessionPlannedMeal
        .where("plannedMealId", "=", args.plannedMealId)
        .where("scopeId", "=", args.scopeId),
    );
    for (const link of links) {
      if (await canEditContents(tx, actor, link.mealPlanningSessionId)) {
        return link.mealPlanningSessionId;
      }
    }
    return null;
  }
}
